import { Message } from '../components/AriaChat/types/chat';

export type BackgroundStreamStatus = 'streaming' | 'complete' | 'error';

export interface BackgroundStreamEntry {
  requestId: string;
  sessionId: string;
  messages: Message[];
  status: BackgroundStreamStatus;
  detached: boolean;
  notifyOnComplete: boolean;
  title?: string;
  urlToken?: string;
  hasUnreadResponse: boolean;
}

type Listener = () => void;

class BackgroundStreamRegistry {
  private byRequestId = new Map<string, BackgroundStreamEntry>();
  private bySessionId = new Map<string, string>();
  private listeners = new Set<Listener>();
  // Sessions read this app session. Suppresses a stale server `hasUnreadResponse: true` that a
  // refetch may return before the mark-read lands, which would flicker the dot back after opening.
  private locallyReadSessions = new Set<string>();

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notify(): void {
    this.listeners.forEach((l) => l());
  }

  private linkSession(requestId: string, sessionId: string): void {
    const entry = this.byRequestId.get(requestId);
    if (!entry) return;
    if (entry.sessionId && entry.sessionId !== sessionId) {
      this.bySessionId.delete(entry.sessionId);
    }
    entry.sessionId = sessionId;
    this.bySessionId.set(sessionId, requestId);
  }

  startStream(requestId: string, messages: Message[], sessionId = ''): void {
    const entry: BackgroundStreamEntry = {
      requestId,
      sessionId,
      messages,
      status: 'streaming',
      detached: false,
      notifyOnComplete: false,
      hasUnreadResponse: false,
    };
    this.byRequestId.set(requestId, entry);
    if (sessionId) {
      this.bySessionId.set(sessionId, requestId);
    }
    this.notify();
  }

  setSessionId(requestId: string, sessionId: string, meta?: { title?: string; urlToken?: string }): void {
    this.linkSession(requestId, sessionId);
    const entry = this.byRequestId.get(requestId);
    if (!entry) return;
    if (meta?.title) entry.title = meta.title;
    if (meta?.urlToken) entry.urlToken = meta.urlToken;
    this.notify();
  }

  updateMessages(requestId: string, messages: Message[]): void {
    const entry = this.byRequestId.get(requestId);
    if (!entry) return;
    entry.messages = messages;
    this.notify();
  }

  getByRequestId(requestId: string): BackgroundStreamEntry | undefined {
    return this.byRequestId.get(requestId);
  }

  getBySessionId(sessionId: string): BackgroundStreamEntry | undefined {
    const requestId = this.bySessionId.get(sessionId);
    return requestId ? this.byRequestId.get(requestId) : undefined;
  }

  isSessionStreaming(sessionId: string): boolean {
    const entry = this.getBySessionId(sessionId);
    return entry?.status === 'streaming';
  }

  isSessionUnread(sessionId: string): boolean {
    const entry = this.getBySessionId(sessionId);
    return !!entry?.hasUnreadResponse;
  }

  getStreamingSessionIds(): string[] {
    return Array.from(this.byRequestId.values())
      .filter((e) => e.status === 'streaming' && e.sessionId)
      .map((e) => e.sessionId);
  }

  detach(requestId: string, notifyOnComplete = true): void {
    const entry = this.byRequestId.get(requestId);
    if (!entry) return;
    if (entry.detached && entry.notifyOnComplete === notifyOnComplete) return;
    entry.detached = true;
    entry.notifyOnComplete = notifyOnComplete;
    this.notify();
  }

  isSessionLocallyRead(sessionId: string): boolean {
    return this.locallyReadSessions.has(sessionId);
  }

  complete(requestId: string, messages: Message[], markUnread: boolean): void {
    const entry = this.byRequestId.get(requestId);
    if (!entry) return;
    entry.messages = messages;
    entry.status = 'complete';
    if (markUnread) {
      entry.hasUnreadResponse = true;
      // A genuinely new response arrived, so this session is unread again.
      if (entry.sessionId) this.locallyReadSessions.delete(entry.sessionId);
    }
    this.notify();
  }

  markSessionRead(sessionId: string): void {
    if (sessionId) this.locallyReadSessions.add(sessionId);
    const entry = this.getBySessionId(sessionId);
    if (entry) entry.hasUnreadResponse = false;
    this.notify();
  }

  error(requestId: string): void {
    const entry = this.byRequestId.get(requestId);
    if (!entry) return;
    entry.status = 'error';
    this.notify();
  }

  reattach(sessionId: string): void {
    const entry = this.getBySessionId(sessionId);
    if (!entry || !entry.detached) return;
    entry.detached = false;
    this.notify();
  }

  remove(requestId: string): void {
    const entry = this.byRequestId.get(requestId);
    if (entry?.sessionId) {
      this.bySessionId.delete(entry.sessionId);
    }
    this.byRequestId.delete(requestId);
    this.notify();
  }

  getAllEntries(): BackgroundStreamEntry[] {
    return Array.from(this.byRequestId.values());
  }

  async detachAllStreaming(): Promise<void> {
    const { registerBackgroundStream } = await import(
      '../components/AriaChat/utils/chatSessionUtil'
    );
    for (const entry of this.byRequestId.values()) {
      if (entry.status === 'streaming' && !entry.detached) {
        entry.detached = true;
        entry.notifyOnComplete = true;
        try {
          await registerBackgroundStream(entry.requestId, entry.sessionId || undefined);
        } catch (e) {
          console.error('Failed to register background stream:', e);
        }
      }
    }
    this.notify();
  }
}

export const backgroundStreamRegistry = new BackgroundStreamRegistry();
